import React, { useMemo, useCallback } from 'react';
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Box,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material';

import { IItems } from 'api/catalog/models';
import { DomId } from 'constants/DomId';
import { ProductListingColumn, useProductListing, useSizes } from 'hooks';
import { usePaging } from 'hooks/usePaging';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from 'constants/table';
import NoResults from 'ui/ErrorDisplay/NoResults';

import ProductDataCell from './ProductDataCell';

interface IProps {
  isLoading?: boolean;
  results?: IItems;
  tableHeight?: string;
  isFixedHeight?: boolean;
  paginationContentArea?: React.ReactNode;
}

const ProductListing = ({
  isLoading = false,
  results,
  tableHeight,
  isFixedHeight = false,
  paginationContentArea = null,
}: IProps) => {
  const theme = useTheme();
  const { columns } = useProductListing();
  const { combinedHeight } = useSizes();
  const {
    handleChangePage,
    handleChangeRowsPerPage,
    handleChangeSort,
    sortInfo,
  } = usePaging(PAGE_SIZES);
  const { sortBy, sortDirection } = sortInfo;

  const fixedHeight = useMemo(
    () => (tableHeight ? tableHeight : `calc(100vh - ${combinedHeight}px)`),
    [combinedHeight, tableHeight]
  );

  const tableStyles = useMemo(
    () => (isFixedHeight ? { maxHeight: fixedHeight } : {}),
    [fixedHeight, isFixedHeight]
  );

  const materialSortDirection: 'asc' | 'desc' = useMemo(
    () => (sortDirection === 'asc' ? 'asc' : 'desc'),
    [sortDirection]
  );

  const getSortLabel = useCallback(
    (column: ProductListingColumn) =>
      column.customSortLabel ? column.customSortLabel : column.id,
    []
  );

  const handleSort = useCallback(
    (column: ProductListingColumn) => {
      const sortLabel = getSortLabel(column);
      if (sortLabel === sortBy) {
        handleChangeSort([sortLabel, sortDirection === 'asc' ? 'desc' : 'asc']);
      } else {
        handleChangeSort([
          sortLabel,
          column.initialSort ? column.initialSort : 'asc',
        ]);
      }
    },
    [getSortLabel, handleChangeSort, sortBy, sortDirection]
  );

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={tableStyles}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    fontWeight: 'bold',
                    minWidth: column.minWidth,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                  sortDirection={
                    column.isSortable && getSortLabel(column) === sortBy
                      ? materialSortDirection
                      : undefined
                  }
                >
                  {column.isSortable ? (
                    <TableSortLabel
                      active={getSortLabel(column) === sortBy}
                      direction={
                        getSortLabel(column) === sortBy
                          ? materialSortDirection
                          : 'asc'
                      }
                      sx={{
                        textDecoration: column.isSortable
                          ? 'underline'
                          : 'none',
                      }}
                      onClick={() => handleSort(column)}
                    >
                      {column.friendlyName}
                    </TableSortLabel>
                  ) : (
                    column.friendlyName
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <LinearProgress sx={{ width: '100%' }} />
                </TableCell>
              </TableRow>
            ) : !results?.items?.length ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <NoResults />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {results.items.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.elasticId}>
                      {columns.map((column) => (
                        <ProductDataCell
                          key={column.id}
                          column={column}
                          item={row}
                        />
                      ))}
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display='flex'>
        {paginationContentArea ? (
          <Box display='flex' alignItems='center' marginLeft='15px'>
            {paginationContentArea}
          </Box>
        ) : null}
        <Box flex='1'>
          <TablePagination
            rowsPerPageOptions={PAGE_SIZES}
            component='div'
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            count={results?.page.totalElements || 0}
            rowsPerPage={results?.page.size || DEFAULT_PAGE_SIZE}
            page={results?.page.number || 0}
            onPageChange={(e, newPage) => handleChangePage(newPage)}
            onRowsPerPageChange={(e) => handleChangeRowsPerPage(e.target.value)}
            id={DomId.TablePagination}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ProductListing;
